import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Moment from 'moment'
import { connect, useSelector } from 'react-redux'
import { compose, withProps, withPropsOnChange, withState } from 'recompose'
import { withRouter } from 'react-router-dom'
import { useLocations } from '../../../context/locationsContext'

// Theme Imports
import { GraphHeading } from '../../../theme/Global'

// Components
import NumberOfDaySelector from '../numberOfDaySelector'
import APILoadingSpinnerRenderer from '../../../app/components/apiLoadingSpinner'

import { isAppLoading } from '../../../app/selectors'

// Custom UI Elements
import {
  CurrentNumber,
  LocationsLabel, NextDaysText,
  NotificationsErrorMessage,
  SelectRange,
  StateListWrapper,
  WageNotificationsContainer
} from './styles'


export const WageNotificationRenderer = ({ days, number, onClickWage, setDays, states, isLoading, notificationsError }) => {
  const { legacyMWNotificationsArray, isLoading: locationsIsLoading } = useLocations()
  const [displayNumber, setDisplayNumber] = useState()
  const [displayStates, setDisplayStates] = useState()
  const sessionCompany = useSelector((state) => state.account.company)

  useEffect(() => {
    let subscribed = true
   
    const locations = new Set()
    const stateList = {}

    legacyMWNotificationsArray.forEach((notification) => {
      if (Moment(notification.date)
        .isAfter(new Date())) {
        if (Moment(notification.date)
          .isBefore(Moment()
            .add(days, 'days'))) {
          notification.locations.forEach((loc) => locations.add(loc))
          for (const topic in notification.wages) {
            if (notification.wages[topic].new && notification.wages[topic].new.state) {
              stateList[notification.wages[topic].new.state] = true
            }
          }
        }
      }
    })

    if (subscribed && Object.keys(stateList).length > 0) {
      setDisplayStates(Object.keys(stateList)
        .sort())
      setDisplayNumber(Array.from(locations).length)
    }

    return (() => subscribed = false)
  }, [sessionCompany, legacyMWNotificationsArray])

  const showLoadingSpinner = isLoading || locationsIsLoading

  return (
    <WageNotificationsContainer data-testid="WageNotificationRenderer-Paper">
      <GraphHeading data-testid="">
        Wage Notifications
      </GraphHeading>
      {showLoadingSpinner ? <APILoadingSpinnerRenderer /> : null}
      {notificationsError && !isLoading && (
        <NotificationsErrorMessage data-testid="NotificationsErrorMessage">
          Sorry, we’re having some trouble retrieving Wage Notifications.
          Please try refreshing the page or contact your Account Manager for help.
        </NotificationsErrorMessage>
      )}
      {!notificationsError && !showLoadingSpinner && (
        <React.Fragment>
          <CurrentNumber onClick={onClickWage} data-testid="CurrentNumber">
            {displayNumber || number}
          </CurrentNumber>
          <LocationsLabel>
            Locations
          </LocationsLabel>
          <StateListWrapper>
            {displayStates ? displayStates.join(', ') : states.join(', ')}
          </StateListWrapper>
          <SelectRange data-testid="SelectRange">
            <NumberOfDaySelector
              days={30}
              active={days === 30}
              onClick={() => {
                setDays(30)
              }}
            />
            <NumberOfDaySelector
              days={60}
              active={days === 60}
              onClick={() => {
                setDays(60)
              }}
            />
            <NumberOfDaySelector
              days={90}
              active={days === 90}
              onClick={() => {
                setDays(90)
              }}
            />
          </SelectRange>
          <NextDaysText>
            Next Days
          </NextDaysText>
        </React.Fragment>
      )}
    </WageNotificationsContainer>
  )
}

WageNotificationRenderer.propTypes = {
  days: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  setDays: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  states: PropTypes.array.isRequired
}

WageNotificationRenderer.defaultProps = {}

const mapStateToProps = (state) => ({
  notifications: state.minWage.notifications,
  notificationsError: state.minWage.notificationsError,
  isLoading: isAppLoading(state)
})

const WageNotificationComponent = compose(
  withRouter,
  connect(mapStateToProps),
  withState('days', 'setDays', 90),
  withState('number', 'setNumber', 0),
  withState('states', 'setStates', []),
  withProps((props) => ({
    onClickWage: (e) => {
      props.history.push(`/minWage/notifications?dateRange=${props.days}`)
    }
  })),
  withPropsOnChange(['days', 'isLoading', 'notifications'], (props) => {
    // Calculate # of upcoming notifications
    const count = 0
    const stateList = {}
    const states = Object.keys(stateList)
    states.sort()
    return {
      number: count,
      states
    }
  })
)(WageNotificationRenderer)

export default WageNotificationComponent
